// Permanent Delete (purge) dialog for the catalog reference entities.
//
// This is the Song Library permanent-delete modal (src/pages/songs.tsx
// ~1447-1584) generalised over the entity noun: same phases, same
// preflight-then-confirm sequence, same "Will remove" / "Will retain forever"
// disclosure, same typed `DELETE` token plus mandatory reason, same button
// states. Reachable only from Archived.

import React from 'react';
import { Alert } from 'react-bootstrap';
import { Modal, Button as AntdButton } from 'antd';
import { Link } from 'react-router-dom';
import FormItem from '@/common/FormItem/FormItem';
import { EntityRefBadge } from '@/components/EntityRef/EntityRefBadge';
import {
  DELETE_CONFIRM_TOKEN,
  type CatalogEntityKind,
  type CatalogPurgePreflight,
} from './catalogLifecycleTypes';
import { catalogCopy } from './catalogEntityCopy';

export type CatalogPurgePhase =
  | 'idle'
  | 'preflight'
  | 'confirm'
  | 'running'
  | 'done'
  | 'error';

interface CatalogPermanentDeleteModalProps {
  kind: CatalogEntityKind;
  open: boolean;
  phase: CatalogPurgePhase;
  preflight: CatalogPurgePreflight | null;
  errorMessage: string | null;
  /** Name of the single target; ignored when bulkCount > 1. */
  targetName: string | undefined;
  targetId: string | undefined;
  /** 0 or 1 renders the single-target copy; >1 renders bulk copy. */
  bulkCount: number;
  confirmTyped: string;
  onConfirmTypedChange: (value: string) => void;
  reason: string;
  onReasonChange: (value: string) => void;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function CatalogPermanentDeleteModal({
  kind,
  open,
  phase,
  preflight,
  errorMessage,
  targetName,
  targetId,
  bulkCount,
  confirmTyped,
  onConfirmTypedChange,
  reason,
  onReasonChange,
  loading,
  onCancel,
  onConfirm,
}: CatalogPermanentDeleteModalProps) {
  const { noun, nounPlural, Noun, refKind } = catalogCopy(kind);
  const isBulk = bulkCount > 1;
  const referencingSongs = preflight?.referencingSongs ?? [];

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={560}
      destroyOnClose
    >
      <div style={{ padding: 24 }} data-testid={`${kind}-permanent-delete-modal`}>
        <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 12 }}>
          Permanently delete{' '}
          {isBulk ? `${bulkCount} ${nounPlural}` : `"${targetName || ''}"`}?
        </div>

        {phase === 'preflight' ? (
          <div style={{ color: '#666', marginBottom: 16 }}>
            Loading dependency summary…
          </div>
        ) : null}

        {errorMessage ? (
          <Alert variant="danger" className="mb-3">
            {errorMessage}
          </Alert>
        ) : null}

        {preflight ? (
          <div
            style={{ color: '#444', fontSize: 14, marginBottom: 16, textAlign: 'left' }}
          >
            {!isBulk ? (
              <div style={{ marginBottom: 8 }}>
                <div>
                  <strong>{Noun}:</strong> {preflight.name}
                </div>
                <div>
                  <strong>{Noun} ID:</strong>{' '}
                  <EntityRefBadge kind={refKind} id={preflight.id || targetId || ''} />
                </div>
              </div>
            ) : null}

            <div style={{ marginBottom: 8 }}>
              <strong>Will remove</strong>
              <ul style={{ marginBottom: 8 }}>
                {preflight.counts.map((count) => (
                  <li key={count.label}>
                    {count.label}: {count.value}
                  </li>
                ))}
                {preflight.willRemove.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>

              {referencingSongs.length > 0 ? (
                <Alert variant="warning" className="mb-3">
                  <strong>Living song references detected</strong>
                  <ul style={{ marginBottom: 0, marginTop: 8 }}>
                    {referencingSongs.slice(0, 12).map((song, index) => (
                      <li key={song.trackId || `${song.title}-${index}`}>
                        {song.title || song.trackId}
                        {song.status ? ` (${song.status})` : ''}
                      </li>
                    ))}
                  </ul>
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    A {noun} cannot be permanently deleted while a living song
                    still references it. Reassign or permanently delete those
                    songs first
                    {preflight.songLibraryPath ? (
                      <>
                        {' — '}
                        <Link to={preflight.songLibraryPath}>
                          open them in Song Library
                        </Link>
                      </>
                    ) : null}
                    .
                  </div>
                </Alert>
              ) : null}

              <strong>Will retain forever</strong>
              <ul>
                {(preflight.willRetain || []).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>

            {!preflight.eligible ? (
              <Alert variant="warning">
                Deletion blocked:{' '}
                {preflight.blockers.map((blocker) => blocker.message).join(' ')}
              </Alert>
            ) : null}
          </div>
        ) : null}

        {phase === 'confirm' || phase === 'running' || phase === 'error' ? (
          <>
            <FormItem label={`Type ${DELETE_CONFIRM_TOKEN} to confirm`} layout="vertical">
              <input
                className="form-control"
                value={confirmTyped}
                onChange={(e) => onConfirmTypedChange(e.target.value)}
                disabled={loading}
                autoComplete="off"
                data-testid={`${kind}-purge-confirm-input`}
              />
            </FormItem>
            <FormItem label="Deletion reason" layout="vertical">
              <input
                className="form-control"
                value={reason}
                onChange={(e) => onReasonChange(e.target.value)}
                disabled={loading}
                placeholder={`Why is this ${noun} being permanently deleted?`}
                data-testid={`${kind}-purge-reason-input`}
              />
            </FormItem>
          </>
        ) : null}

        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}
        >
          <AntdButton disabled={loading} onClick={onCancel}>
            Cancel
          </AntdButton>
          <AntdButton
            type="primary"
            danger
            loading={loading || phase === 'preflight'}
            disabled={
              !preflight?.eligible ||
              confirmTyped !== DELETE_CONFIRM_TOKEN ||
              !reason.trim()
            }
            onClick={onConfirm}
            data-testid={`${kind}-purge-submit`}
          >
            {phase === 'running' ? 'Deleting…' : 'Permanently Delete'}
          </AntdButton>
        </div>
      </div>
    </Modal>
  );
}
